import { AxiosResponse } from "axios";
import apiClient from "../../../../shared/infrastructure/api/client";
import { TherapeuticPlan, TherapeuticPlanAnalysis } from "../../domain/TherapeuticPlan";

/**
 * The API returns the AI analysis as flat fields (`summary`, `path`, `themes`,
 * `recommendations`), while the UI consumes it as a nested `analysis` object.
 */
interface TherapeuticPlanApiResponse extends Omit<TherapeuticPlan, "analysis"> {
  summary?: string | null;
  path?: string | null;
  themes?: string[] | null;
  recommendations?: string[] | null;
}

const withNestedAnalysis = (
  response: AxiosResponse<TherapeuticPlanApiResponse>
): AxiosResponse<TherapeuticPlan> => {
  const { summary, path, themes, recommendations, ...plan } = response.data;

  const hasAnalysis =
    Boolean(summary) ||
    Boolean(path) ||
    (themes?.length ?? 0) > 0 ||
    (recommendations?.length ?? 0) > 0;

  return {
    ...response,
    data: {
      ...plan,
      analysis: hasAnalysis
        ? {
            summary: summary ?? "",
            path: path ?? "",
            themes: themes ?? [],
            recommendations: recommendations ?? [],
          }
        : undefined,
    },
  };
};

interface ListTherapeuticPlansParams {
  offset?: number;
  limit?: number;
  from_date?: Date;
  to_date?: Date;
  patient_name?: string;
}

interface ListPatientTherapeuticPlansParams {
  offset?: number;
  limit?: number;
  from_date?: Date;
  to_date?: Date;
}

interface ListTherapeuticPlansResponse {
  therapeutic_plans: TherapeuticPlan[];
  total: number;
}

export const TherapeuticPlansAPI = {
  get: (id: string): Promise<AxiosResponse<TherapeuticPlan>> =>
    apiClient.get(`/therapeutic_plans/${id}`).then(withNestedAnalysis),
  create: (data: Partial<TherapeuticPlan>): Promise<AxiosResponse<TherapeuticPlan>> => apiClient.post("/therapeutic_plans/", data),
  update: (id: string, data: Partial<TherapeuticPlan>): Promise<AxiosResponse<TherapeuticPlan>> =>
    apiClient.patch(`/therapeutic_plans/${id}`, data).then(withNestedAnalysis),
  delete: (id: string): Promise<AxiosResponse<void>> => apiClient.delete(`/therapeutic_plans/${id}`),

  list_by_therapist: (id: string, params?: ListTherapeuticPlansParams): Promise<AxiosResponse<ListTherapeuticPlansResponse>> =>
    apiClient.get(`/therapeutic_plans/therapist/${id}`, { params }),

  list_by_patient: (id: string, params?: ListPatientTherapeuticPlansParams): Promise<AxiosResponse<ListTherapeuticPlansResponse>> =>
    apiClient.get(`/therapeutic_plans/patient/${id}`, { params }),

  list_by_couple_patient: (id: string, params?: ListPatientTherapeuticPlansParams): Promise<AxiosResponse<ListTherapeuticPlansResponse>> =>
    apiClient.get(`/therapeutic_plans/couple-patient/${id}`, { params }),

  analyze: (id: string): Promise<AxiosResponse<TherapeuticPlanAnalysis>> => apiClient.patch(`/therapeutic_plans/analyze/${id}`),
};
